import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getCookie, setCookie, getCookieOpts } from '../lib/cookies';

// Partner Portal session state — deliberately SEPARATE from UserContext: a
// portal login must never touch the app session (and vice versa). The token
// lives in its own cookie and is attached ONLY to /portal API requests (see the
// axios interceptor in MainRouter). localStorage holds display data only —
// never the token, never PII beyond the partner's own name.

const PORTAL_TOKEN_COOKIE = 'portal_token';
const PORTAL_USER_KEY = 'portalUser';
const PORTAL_SESSION_DAYS = 30;

const PortalContext = createContext(null);

const readStoredUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem(PORTAL_USER_KEY)) || null;
  } catch (e) {
    return null;
  }
};

export const PortalProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [portalToken, setPortalToken] = useState(() => getCookie(PORTAL_TOKEN_COOKIE) || null);
  const [portalUser, setPortalUser] = useState(readStoredUser);

  const portalSignIn = useCallback(
    ({ token, displayName, status }) => {
      // Defensive: never let a previous partner's cached queries render for a
      // new login on the same browser.
      queryClient.removeQueries(['portal']);
      setCookie(PORTAL_TOKEN_COOKIE, token, {
        ...getCookieOpts(),
        maxAge: 60 * 60 * 24 * PORTAL_SESSION_DAYS,
      });
      const user = { displayName, status };
      window.localStorage.setItem(PORTAL_USER_KEY, JSON.stringify(user));
      setPortalToken(token);
      setPortalUser(user);
    },
    [queryClient],
  );

  const portalSignOut = useCallback(() => {
    // removeCookie() can't delete this cookie (it omits the path/domain the
    // cookie was SET with) — overwrite with matching options + maxAge 0, the
    // same approach the app's own logout uses.
    setCookie(PORTAL_TOKEN_COOKIE, '', { ...getCookieOpts(), maxAge: 0 });
    // setCookie mirrors values into localStorage as a fallback — clear that too.
    window.localStorage.removeItem(`cookie_${PORTAL_TOKEN_COOKIE}`);
    window.localStorage.removeItem(PORTAL_USER_KEY);
    // Drop every cached portal query: money + referral data must never survive
    // into another partner's session on a shared browser.
    queryClient.removeQueries(['portal']);
    setPortalToken(null);
    setPortalUser(null);
  }, [queryClient]);

  // Keep the cached status bucket fresh (e.g. an applicant approved while
  // logged in flips pending -> active and unlocks the full portal). Must work
  // even when portalUser is missing (token survived but storage was cleared) —
  // returning prev there caused a /portal <-> /portal/status redirect loop.
  const updatePortalStatus = useCallback((status) => {
    setPortalUser((prev) => {
      if (prev && prev.status === status) return prev;
      const next = prev ? { ...prev, status } : { status };
      window.localStorage.setItem(PORTAL_USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ portalToken, portalUser, portalSignIn, portalSignOut, updatePortalStatus }),
    [portalToken, portalUser, portalSignIn, portalSignOut, updatePortalStatus],
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

export const usePortal = () => {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used within PortalProvider');
  return ctx;
};

export { PORTAL_TOKEN_COOKIE };
